import {
  getFamilyOpportunityIntelligenceReport,
  getPatientBaseHealthReport,
  getPatientRiskComplianceReport,
  getPatientValueRevenueReport,
} from '@services/api/reports/patients.api'
import {
  getCommunicationCenterPerformanceReport,
  getRecallReactivationControlReport,
  getScheduleReliabilityReport,
  getTreatmentConversionFunnelReport,
} from '@services/api/reports/engagement.api'
import {
  getChairRoomUtilizationReport,
  getHygienePerformanceReport,
  getScheduledAppointmentsReport,
} from '@services/api/reports/operations.api'
import {
  getClinicalMixTreatmentProfileReport,
  getCollectionsFinancialEfficiencyReport,
  getEfficiencyScorecardReport,
  getProductionPerformanceReport,
  getTreatmentEffectivenessReport,
} from '@services/api/reports/providerPerformance.api'
import {
  getClaimsPerformanceReport,
  getGoalAttainmentVarianceReport,
  getReceivablesRiskAnalysisReport,
  getRevenueRealizationAnalyticsReport,
  type ReportQueryParams,
} from '@services/api/reports/finance.api'
import { mapToolbarToFinanceQuery } from '@utils/reports/revenueRealizationFinance.util'
import {
  buildReportQueryCacheKey,
  getReportQueryInflight,
  joinReportQueryInflight,
  leaveReportQueryInflight,
  readReportQueryCache,
  setReportQueryInflight,
  writeReportQueryCache,
} from '@utils/reportQueryCache.util'

export type ReportMetricPayload = {
  data?: Record<string, unknown>[]
  data2?: Record<string, unknown>[]
  extendedData?: Record<string, unknown>[][]
}

export type ReportPayloadData = Record<string, ReportMetricPayload>

type MetricSchemaItem = { id: string; type?: string }
type ReportFetcher = (params: ReportQueryParams) => Promise<unknown>
type ProgressHandler = (data: ReportPayloadData) => void
type ReportRow = Record<string, unknown>
type ReportSection = {
  buckets?: ReportRow[]
  rows?: ReportRow[]
  patients?: ReportRow[]
  categories?: ReportRow[]
  segments?: ReportRow[]
  stages?: ReportRow[]
  bars?: ReportRow[]
  value?: unknown
  gap?: unknown
  requiredPace?: unknown
  runRate?: unknown
  insurance?: { amount?: unknown }
  patient?: { amount?: unknown }
}
type ReportSource = Record<string, ReportSection>

const REPORT_FETCHERS: Record<string, ReportFetcher> = {
  'patient-base-health': getPatientBaseHealthReport,
  'patient-value-rev-distribution': getPatientValueRevenueReport,
  'patient-risk-compliance': getPatientRiskComplianceReport,
  'family-opportunity-intelligence': getFamilyOpportunityIntelligenceReport,
  'recall-reactivation-control': getRecallReactivationControlReport,
  'schedule-reliability': getScheduleReliabilityReport,
  'treatment-conversion-funnel': getTreatmentConversionFunnelReport,
  'communication-center-performance': getCommunicationCenterPerformanceReport,
  'scheduled-appointments': getScheduledAppointmentsReport,
  'chair-room-utilization': getChairRoomUtilizationReport,
  'hygiene-performance': getHygienePerformanceReport,
  'production-performance': getProductionPerformanceReport,
  'collections-financial-efficiency': getCollectionsFinancialEfficiencyReport,
  'clinical-mix-treatment-profile': getClinicalMixTreatmentProfileReport,
  'treatment-effectiveness': getTreatmentEffectivenessReport,
  'efficiency-scorecard': getEfficiencyScorecardReport,
  'revenue-realization-analytics': getRevenueRealizationAnalyticsReport,
  'receivables-risk-analysis': getReceivablesRiskAnalysisReport,
  'claims-performance': getClaimsPerformanceReport,
  'goal-attainment-variance': getGoalAttainmentVarianceReport,
}

export const SUPPORTED_REPORT_TYPES = Object.freeze(
  Object.keys(REPORT_FETCHERS),
)

async function loadCachedReport(
  fetcher: ReportFetcher,
  query: ReportQueryParams,
): Promise<unknown> {
  const cacheKey = buildReportQueryCacheKey(
    fetcher,
    query as unknown as Record<string, unknown>,
  )
  const cached = readReportQueryCache<unknown>(cacheKey)
  if (cached !== undefined) return cached

  const existing = getReportQueryInflight<unknown>(cacheKey)
  if (existing && joinReportQueryInflight(cacheKey)) {
    try {
      return await existing
    } finally {
      leaveReportQueryInflight(cacheKey, existing)
    }
  }

  const request = fetcher(query).then((result) => {
    writeReportQueryCache(cacheKey, result)
    return result
  })
  return setReportQueryInflight(cacheKey, request)
}

const number = (value: unknown): string =>
  Number(value ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 })
const currency = (value: unknown): string =>
  Number(value ?? 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
const percent = (value: unknown): string => `${number(value)}%`
const date = (value: unknown): string => {
  if (!value) return '—'
  const parsed = new Date(String(value))
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleDateString('en-US')
}
const scalarRows = (
  values: Array<[string, unknown, ('number' | 'currency' | 'percent')?]>,
): Record<string, unknown>[] =>
  values.map(([metric, value, format = 'number']) => ({
    metric,
    value:
      format === 'currency'
        ? currency(value)
        : format === 'percent'
          ? percent(value)
          : number(value),
  }))
const seriesRows = (
  series: ReportSection,
  valueKey: string,
  formatter: (value: unknown) => string = number,
): Record<string, unknown>[] =>
  (series?.buckets ?? []).map((bucket: ReportRow) => ({
    month: bucket.label,
    [valueKey]: formatter(bucket.value),
  }))

function normalizeReportSource(
  type: string,
  report: ReportSource,
): ReportPayloadData {
  switch (type) {
    case 'patient-base-health': {
      const visits = report.lastVisitDistribution?.buckets ?? []
      const visitTotal = visits.reduce(
        (sum: number, item: ReportRow) => sum + Number(item.value ?? 0),
        0,
      )
      return {
        summary: {
          data: scalarRows([
            ['Total active patients', report.totalActivePatients?.value],
            ['Net active growth', report.netActiveGrowth?.value],
            ['Retention rate', report.retentionRate?.value, 'percent'],
            ['Reactivation rate', report.reactivationRate?.value, 'percent'],
            ['At-risk patients', report.atRiskPatientCount?.value],
          ]),
        },
        lastVisitDistribution: {
          data: visits.map((item: ReportRow) => ({
            recency: item.label,
            percentage: percent(
              visitTotal > 0 ? (Number(item.value ?? 0) / visitTotal) * 100 : 0,
            ),
          })),
        },
        patientPopulationTrend: {
          data: (report.patientPopulationTrend?.buckets ?? []).map(
            (item: ReportRow) => ({
              month: item.label,
              active: number(item.active),
              dormant: number(item.dormant),
              lost: number(item.lost),
            }),
          ),
        },
        patientAgeDistribution: {
          data: (report.atRiskByAge?.buckets ?? []).map((item: ReportRow) => ({
            age: item.label,
            percentage: percent(item.percentage),
          })),
        },
        highRiskPatients: {
          data: (report.atRiskPatients?.patients ?? []).map(
            (item: ReportRow) => ({
              patientName: item.patientName,
              lastVisitDaysAgo: number(item.lastVisitDaysAgo),
              unscheduledTreatment: currency(item.unscheduledTreatment),
              noShowCount: number(item.noShowCount),
              outstandingBalance: currency(item.outstandingBalance),
              riskScore: number(item.riskScore),
            }),
          ),
        },
      }
    }
    case 'patient-value-rev-distribution':
      return {
        pvSummary: {
          data: scalarRows([
            [
              'Average patient value',
              report.averagePatientValue?.value,
              'currency',
            ],
            [
              'Active patient revenue',
              report.activePatientRevenue?.value,
              'currency',
            ],
            [
              'Unscheduled treatment',
              report.unscheduledTreatment?.value,
              'currency',
            ],
            [
              'Total outstanding AR',
              report.totalOutstandingAr?.value,
              'currency',
            ],
          ]),
        },
        pvProductionMix: {
          data: (report.productionByProcedureMix?.categories ?? []).map(
            (item: ReportRow) => ({
              category: item.label,
              revenue: currency(item.amount),
              percentage: percent(item.percentage),
            }),
          ),
          data2: (report.activePatientValueSegments?.segments ?? []).map(
            (item: ReportRow) => ({
              range: item.label,
              patientCount: number(item.count),
              patientsPct: percent(item.percentage),
            }),
          ),
        },
        pvNewVsExisting: {
          data: (report.newVsExistingProduction?.rows ?? []).map(
            (item: ReportRow) => ({
              category: item.label,
              revenue: currency(item.revenue),
              patientCount: number(item.patients),
              avgRevenuePerPatient: currency(
                Number(item.patients)
                  ? Number(item.revenue) / Number(item.patients)
                  : 0,
              ),
            }),
          ),
        },
        pvArOpportunity: {
          data: (report.arUnscheduledByProvider?.rows ?? []).map(
            (item: ReportRow) => ({
              provider: item.providerName,
              patients: number(item.patients),
              unscheduledValue: currency(item.unscheduledValue),
              outstandingAr: currency(item.outstandingAr),
            }),
          ),
        },
      }
    case 'patient-risk-compliance':
      return {
        prSummary: {
          data: scalarRows([
            [
              'Patients with unscheduled treatment',
              report.patientsWithUnscheduledTreatments?.value,
            ],
            [
              'Patients with medical alerts',
              report.patientsWithMedicalAlerts?.value,
            ],
            ['Patients overdue', report.patientsOverdue?.value],
          ]),
        },
        prUnscheduled: {
          data: (report.unscheduledByProductionBucket?.buckets ?? []).map(
            (item: ReportRow) => ({
              bucket: item.label,
              patients: number(item.patients),
              totalValue: currency(item.revenue),
            }),
          ),
        },
        prOverdue: {
          data: (report.overdueVisitGrouping?.buckets ?? []).map(
            (item: ReportRow) => ({
              bucket: item.label,
              patients: number(item.count),
              pctActive: percent(item.percentage),
            }),
          ),
        },
        prMedicalAlerts: {
          data: (report.medicalAlertCategories?.rows ?? []).map(
            (item: ReportRow) => ({
              category: item.category,
              patients: number(item.patients),
            }),
          ),
        },
      }
    case 'family-opportunity-intelligence':
      return {
        foSummary: {
          data: scalarRows([
            [
              'Same-family opportunities',
              report.sameFamilyOpportunities?.value,
            ],
            ['Households with gaps', report.householdsWithGaps?.value],
            ['Upcoming birthdays', report.upcomingBirthdays?.value],
            [
              'Multi-family scheduling gaps',
              report.multiFamilySchedulingGaps?.value,
            ],
          ]),
        },
        foBirthdays: {
          data: (report.birthdayMilestones?.rows ?? []).map(
            (item: ReportRow) => ({
              patient: item.patientName,
              date: date(item.date),
              household: item.household,
              actions: '',
            }),
          ),
        },
        foMultiFamily: {
          data: (report.multiFamilyGapBySpread?.buckets ?? []).map(
            (item: ReportRow) => ({
              gapBucket: item.label,
              households: number(item.count),
            }),
          ),
        },
        foHouseholdGaps: {
          data: (report.householdSchedulingGaps?.rows ?? []).map(
            (item: ReportRow) => ({
              household: item.household,
              members: item.members,
              dueUnscheduled: item.dueUnscheduled,
              recommendedAction: '',
            }),
          ),
        },
      }
    case 'recall-reactivation-control':
      return {
        rrSummary: {
          data: scalarRows([
            ['Recall compliance', report.recallCompliance?.value, 'percent'],
            ['Reactivation rate', report.reactivationRate?.value, 'percent'],
            ['Patients due for recall', report.patientsDueForRecall?.value],
            ['Hygiene overdue', report.hygieneOverdue?.value],
          ]),
        },
        rrRecallTrend: {
          data: seriesRows(report.recallComplianceTrend, 'rate', percent),
        },
        rrProvider: {
          data: (report.providerRecallResponse?.rows ?? []).map(
            (item: ReportRow) => ({
              provider: item.provider,
              eligible: number(item.eligible),
              completed: number(item.completed),
              responseRate: percent(item.ratePct),
            }),
          ),
        },
        rrReactivationTrend: {
          data: (report.reactivationTrend?.buckets ?? []).map(
            (item: ReportRow) => ({
              month: item.label,
              dormantPool: '',
              reactivatedPatients: number(item.count),
              reactivationRate: percent(item.percentage),
            }),
          ),
        },
        rrHygieneOverdue: {
          data: (report.hygieneOverduePatients?.rows ?? []).map(
            (item: ReportRow) => ({
              patient: item.patientName,
              lastHygiene: date(item.lastHygiene),
              daysSince: number(item.daysSinceLastVisit),
              phone: item.phone,
            }),
          ),
        },
      }
    case 'schedule-reliability':
      return {
        srSummary: {
          data: scalarRows([
            ['No-show rate', report.noShowRate?.value, 'percent'],
            ['Cancellation rate', report.cancellationRate?.value, 'percent'],
            ['No-show count', report.noShowCount?.value],
            ['Repeat offenders', report.repeatOffenders?.value],
          ]),
        },
        srRateTrend: {
          data: (report.noShowCancellationTrend?.buckets ?? []).map(
            (item: ReportRow) => ({
              month: item.label,
              scheduledAppointments: '',
              noShows: '',
              noShowRate: percent(item.noShowRate),
              cancellations: '',
              cancelRate: percent(item.cancellationRate),
            }),
          ),
        },
        srNoShowList: {
          data: (report.noShowPatientRows?.rows ?? []).map(
            (item: ReportRow) => ({
              patient: item.patientName,
              date: date(item.apptDate),
              provider: item.provider,
              procedure: item.procedure,
              phone: item.phone,
            }),
          ),
        },
        srRepeatOffenders: {
          data: (report.repeatOffenderRows?.rows ?? []).map(
            (item: ReportRow) => ({
              patient: item.patientName,
              noShows: number(item.noShows),
              cancellations: number(item.cancellations),
              lastEvent: date(item.lastEventDate),
            }),
          ),
        },
        srCancellationPattern: {
          data: (report.cancellationWindowDistribution?.buckets ?? []).map(
            (item: ReportRow) => ({
              window: item.label,
              cancellations: number(item.count),
              share: percent(item.percentage),
            }),
          ),
        },
      }
    case 'treatment-conversion-funnel':
      return {
        tcfSummary: {
          data: scalarRows([
            [
              'Treatment acceptance rate',
              report.treatmentAcceptanceRate?.value,
              'percent',
            ],
            [
              'Unscheduled treatment value',
              report.unscheduledTreatmentValue?.value,
              'currency',
            ],
            ['Exam unscheduled cases', report.examUnscheduledCases?.value],
            [
              'Completed vs diagnosed',
              report.completedVsDiagnosed?.value,
              'percent',
            ],
          ]),
          extendedData: [
            (report.conversionFunnel?.stages ?? []).map((item: ReportRow) => ({
              stage: item.label,
              treatmentValue: currency(item.value),
              patients: number(item.count),
            })),
          ],
        },
        tcfOpportunities: {
          data: (report.unscheduledOpportunities?.buckets ?? []).map(
            (item: ReportRow) => ({
              bucket: item.label,
              patients: number(item.count),
              totalValue: currency(item.value),
            }),
          ),
        },
        tcfProvider: {
          data: (report.examUnscheduledByProvider?.rows ?? []).map(
            (item: ReportRow) => ({
              provider: item.provider,
              examDiagnoses: number(item.examDiagnoses),
              unscheduledCount: number(item.unscheduledCount),
              unscheduledPct: percent(item.unscheduledPct),
            }),
          ),
        },
        tcfMonthly: {
          data: seriesRows(
            report.acceptanceRateTrend,
            'completedRatio',
            percent,
          ),
        },
      }
    case 'communication-center-performance':
      return {
        ccCallsKpiSummary: {
          data: scalarRows([
            ['Total calls', report.totalCalls?.value],
            ['Answered', report.answered?.value],
            ['Missed', report.missed?.value],
            ['Declined', report.declined?.value],
            ['Follow-ups', report.followUps?.value],
          ]),
        },
        ccMessagingKpiSummary: {
          data: scalarRows([
            [
              'Average handling time (seconds)',
              report.avgHandlingTimeSeconds?.value,
            ],
          ]),
        },
        ccEffectiveness: {
          data: (report.communicationEffectiveness?.bars ?? []).map(
            (item: ReportRow) => ({
              metric: item.label,
              value: percent(item.rate),
            }),
          ),
        },
        ccCallsVolume: {
          data: (report.callVolumeOutcomes?.buckets ?? []).map(
            (item: ReportRow) => ({
              period: item.label,
              total: number(item.total),
              answered: number(item.answered),
              missed: number(item.missed),
              declined: '',
              followUps: '',
            }),
          ),
        },
        ccMessaging: {
          data: (report.messagingPerformance?.buckets ?? []).map(
            (item: ReportRow) => ({
              day: item.label,
              inbound: number(item.inbound),
              replied: number(item.replied),
              unreplied: number(
                Math.max(0, Number(item.inbound) - Number(item.replied)),
              ),
            }),
          ),
        },
      }
    case 'scheduled-appointments':
      return {
        saSummary: {
          data: scalarRows([
            ['Patients seen', report.patientsSeen?.value],
            ['Average filled slots', report.avgFilledSlots?.value],
            ['Average open slots', report.avgOpenSlots?.value],
            ['Average hygiene gaps', report.avgHygieneGaps?.value],
            ['No-shows', report.noShows?.value],
          ]),
        },
        saNoShows: { data: seriesRows(report.noShowsByPeriod, 'noShows') },
        saFilledOpen: {
          data: (report.filledSlotsByPeriod?.buckets ?? []).map(
            (item: ReportRow, index: number) => ({
              month: item.label,
              averageFilled: number(item.value),
              averageOpen: number(
                report.openSlotsByPeriod?.buckets?.[index]?.value,
              ),
            }),
          ),
        },
        saHygieneGaps: {
          data: seriesRows(report.hygieneGapsByPeriod, 'averageGaps'),
        },
        saProductionTargetScheduled: {
          data: (report.scheduledProductionByPeriod?.buckets ?? []).map(
            (item: ReportRow, index: number) => ({
              month: item.label,
              scheduledProduction: currency(item.value),
              targetProduction: currency(
                report.productionTargetByPeriod?.buckets?.[index]?.value,
              ),
            }),
          ),
        },
      }
    case 'chair-room-utilization':
      return {
        cruSummary: {
          data: scalarRows([
            ['Chair time used', report.chairTimeUsed?.value],
            ['Chair time available', report.chairTimeAvailable?.value],
            ['Average rooms used', report.avgRoomsUsed?.value],
            ['Average room operations', report.avgRoomOperations?.value],
            ['Utilization rate', report.utilizationRate?.value, 'percent'],
          ]),
        },
        cruChairTime: {
          data: (report.chairTimeUsedByPeriod?.buckets ?? []).map(
            (item: ReportRow, index: number) => ({
              month: item.label,
              chairTimeUsed: number(item.value),
              chairTimeAvailable: number(
                report.chairTimeAvailableByPeriod?.buckets?.[index]?.value,
              ),
            }),
          ),
        },
        cruProvidersWorking: {
          data: seriesRows(report.providersWorkingByPeriod, 'providersWorking'),
        },
        cruOperationsEfficiency: {
          data: seriesRows(
            report.utilizationRateByPeriod,
            'efficiency',
            percent,
          ),
        },
      }
    case 'hygiene-performance':
      return {
        hpSummary: {
          data: scalarRows([
            ['Hygiene visits', report.hygieneVisits?.value],
            ['Hygiene openings', report.hygieneOpenings?.value],
            [
              'Hygiene utilization',
              report.hygieneUtilization?.value,
              'percent',
            ],
            ['Recall compliance', report.recallCompliance?.value, 'percent'],
            [
              'Hygiene reactivation',
              report.hygieneReactivation?.value,
              'percent',
            ],
          ]),
        },
        hpOpenings: {
          data: seriesRows(report.hygieneOpeningsByPeriod, 'averageOpenings'),
        },
        hpUtilization: {
          data: seriesRows(
            report.hygieneUtilizationByPeriod,
            'utilization',
            percent,
          ),
        },
        hpRecallCompliance: {
          data: seriesRows(
            report.recallComplianceByPeriod,
            'compliance',
            percent,
          ),
        },
        hpReactivationSuccess: {
          data: seriesRows(
            report.hygieneReactivationByPeriod,
            'reactivation',
            percent,
          ),
        },
      }
    case 'production-performance':
      return {
        ppSummary: {
          data: scalarRows([
            ['Production', report.production?.value, 'currency'],
            ['Production goal', report.productionGoal?.value, 'currency'],
            [
              'Average production per hour',
              report.avgProductionPerHour?.value,
              'currency',
            ],
            [
              'Average production per day',
              report.avgProductionPerDay?.value,
              'currency',
            ],
          ]),
        },
        ppProduction: {
          data: seriesRows(report.production, 'production', currency),
        },
        ppProductionGoal: {
          data: seriesRows(report.productionGoal, 'goal', currency),
        },
        ppPerHour: {
          data: seriesRows(report.avgProductionPerHour, 'perHour', currency),
        },
        ppPerDay: {
          data: seriesRows(report.avgProductionPerDay, 'perDay', currency),
        },
      }
    case 'collections-financial-efficiency':
      return {
        cfeSummary: {
          data: scalarRows([
            ['Collections', report.collections?.value, 'currency'],
            ['Production', report.production?.value, 'currency'],
            [
              'Net collection percentage',
              report.netCollectionPercentage?.value,
              'percent',
            ],
            ['Same-day payments', report.sameDayPayments?.value, 'currency'],
          ]),
        },
        cfeCollections: {
          data: seriesRows(report.collections, 'collections', currency),
        },
        cfeNetCollection: {
          data: seriesRows(
            report.netCollectionPercentage,
            'netCollection',
            percent,
          ),
        },
        cfeSameDay: {
          data: seriesRows(report.sameDayPayments, 'sameDay', currency),
        },
        cfeProductionRef: {
          data: seriesRows(report.production, 'production', currency),
        },
      }
    case 'clinical-mix-treatment-profile':
      return {
        cmtSummary: {
          data: scalarRows([
            ['Total procedures', report.totalProcedures?.value],
            ['Crown percentage', report.crownPercentage?.value, 'percent'],
            ['SRP percentage', report.srpPercentage?.value, 'percent'],
            [
              'High-value procedure percentage',
              report.highValueProcedurePercentage?.value,
              'percent',
            ],
          ]),
        },
        cmtCrown: { data: seriesRows(report.crownProcedures, 'crownCount') },
        cmtSrp: { data: seriesRows(report.srpProcedures, 'srpCount') },
        cmtFilling: {
          data: seriesRows(report.fillingProcedures, 'fillingCount'),
        },
        cmtExtraction: {
          data: seriesRows(report.extractionProcedures, 'extractionCount'),
        },
      }
    case 'treatment-effectiveness':
      return {
        teSummary: {
          data: scalarRows([
            ['Acceptance rate', report.acceptanceRate?.value, 'percent'],
            [
              'Completed vs diagnosed',
              report.completedVsDiagnosedRate?.value,
              'percent',
            ],
            ['Total diagnosed', report.totalDiagnosed?.value],
            ['Pending treatments', report.pendingTreatments?.value],
          ]),
        },
        teStatusDistribution: {
          data: (report.statusDistribution?.buckets ?? []).map(
            (item: ReportRow) => ({
              metric: item.label,
              value: number(item.value),
            }),
          ),
        },
        teAcceptance: {
          data: seriesRows(report.acceptanceRate, 'acceptanceRate', percent),
        },
        teCompleted: {
          data: seriesRows(
            report.completedVsDiagnosedRate,
            'completionRate',
            percent,
          ),
        },
        teDiagnosed: {
          data: seriesRows(report.totalDiagnosed, 'diagnosedCases'),
        },
        teUnscheduled: {
          data: seriesRows(report.pendingTreatments, 'unscheduledCount'),
        },
      }
    case 'efficiency-scorecard':
      return {
        esSummary: {
          data: scalarRows([
            ['Efficiency score', report.efficiencyScore?.value, 'percent'],
            [
              'Production per hour',
              report.productionPerHour?.value,
              'currency',
            ],
            ['Chair utilization', report.chairUtilization?.value, 'percent'],
            ['Total production', report.totalProduction?.value, 'currency'],
          ]),
        },
        esEfficiency: {
          data: seriesRows(report.efficiencyScore, 'efficiencyScore', percent),
        },
        esProductionPerHour: {
          data: seriesRows(
            report.productionPerHour,
            'productionPerHour',
            currency,
          ),
        },
        esChairUtilization: {
          data: seriesRows(
            report.chairUtilization,
            'chairUtilization',
            percent,
          ),
        },
        esProviderComparison: {
          data: ((report.providers as unknown as ReportRow[]) ?? []).map(
            (item: ReportRow) => ({
              provider: item.providerName,
              production: currency(item.production),
              productionPerHour: currency(item.productionPerHour),
              efficiencyScore: percent(item.efficiencyScore),
              chairUtilization: percent(item.chairUtilization),
            }),
          ),
        },
      }
    case 'revenue-realization-analytics':
      return {
        rraSummary: {
          data: scalarRows([
            ['Total production', report.totalProduction?.value, 'currency'],
            ['Total collections', report.totalCollections?.value, 'currency'],
            ['Net collection', report.netCollection?.value, 'percent'],
            ['Adjustment rate', report.adjustmentRate?.value, 'percent'],
          ]),
        },
        rraProdVsColl: {
          data: (report.production?.buckets ?? []).map(
            (item: ReportRow, index: number) => ({
              month: item.label,
              production: currency(item.value),
              collections: currency(report.collection?.buckets?.[index]?.value),
            }),
          ),
        },
        rraNetCollection: {
          data: seriesRows(
            report.netCollectionPercentByPeriod,
            'netCollection',
            percent,
          ),
        },
        rraAdjustmentRate: {
          data: seriesRows(
            report.adjustmentRateByPeriod,
            'adjustmentRate',
            percent,
          ),
        },
        rraInsuranceVsPatient: {
          data: [
            {
              metric: 'Insurance',
              value: currency(report.insuranceVsPatient?.insurance?.amount),
            },
            {
              metric: 'Patient',
              value: currency(report.insuranceVsPatient?.patient?.amount),
            },
          ],
        },
      }
    case 'receivables-risk-analysis':
      return {
        rriskSummary: {
          data: scalarRows([
            ['Total AR', report.totalAr?.value, 'currency'],
            ['Insurance AR', report.insuranceAr?.value, 'currency'],
            ['Patient AR', report.patientAr?.value, 'currency'],
            ['AR over 90 days', report.arOver90?.value, 'currency'],
            ['Average days in AR', report.averageDaysInAr?.value],
          ]),
        },
        rriskAging: {
          data: seriesRows(report.arAgingBuckets, 'amount', currency).map(
            (row) => ({ bucket: row.month, amount: row.amount }),
          ),
        },
        rriskArTrend: {
          data: seriesRows(report.arTrendByPeriod, 'averageAr', currency),
        },
        rriskAvgDays: {
          data: seriesRows(report.averageDaysInArByPeriod, 'avgDays'),
        },
        rriskHighBalance: {
          data: (report.highBalancePatients?.patients ?? []).map(
            (item: ReportRow) => ({
              patient: item.patientName,
              balance: currency(item.balance),
              daysOutstanding: number(item.daysOutstanding),
            }),
          ),
        },
      }
    case 'claims-performance':
      return {
        claimsSummary: {
          data: scalarRows([
            ['Total claims submitted', report.totalClaimsSubmitted?.value],
            ['Claims pending', report.claimsPending?.value],
            ['Claims overdue', report.claimsOverdue?.value],
            ['Claims denied', report.claimsDenied?.value],
            [
              'Claims acceptance rate',
              report.claimsAcceptanceRate?.value,
              'percent',
            ],
            ['Resubmission rate', report.resubmissionRate?.value, 'percent'],
          ]),
        },
        claimsPaymentTrend: {
          data: seriesRows(report.averageDaysToPaymentByPeriod, 'avgDays'),
        },
        claimsStatus: {
          data: seriesRows(report.claimsStatusOverview, 'count').map((row) => ({
            status: row.month,
            count: row.count,
          })),
        },
        claimsCarrier: {
          data: seriesRows(report.averageDaysToPaymentByCarrier, 'avgDays').map(
            (row) => ({ carrier: row.month, avgDays: row.avgDays }),
          ),
        },
        claimsAging: {
          data: seriesRows(
            report.insuranceArAgingBuckets,
            'amount',
            currency,
          ).map((row) => ({ bucket: row.month, amount: row.amount })),
        },
      }
    case 'goal-attainment-variance': {
      const productionGoals = report.productionGoalByPeriod?.buckets ?? []
      const production = report.productionByPeriod?.buckets ?? []
      const collections = report.collectionByPeriod?.buckets ?? []
      const collectionGoals = report.collectionGoalByPeriod?.buckets ?? []
      const productionGoal = productionGoals.reduce(
        (sum: number, item: ReportRow) => sum + Number(item.value ?? 0),
        0,
      )
      const productionActual = Number(report.totalProduction?.value ?? 0)
      const variance = productionGoal
        ? ((productionActual - productionGoal) / productionGoal) * 100
        : 0
      return {
        gavSummary: {
          data: scalarRows([
            ['Production goal', productionGoal, 'currency'],
            ['Production actual', productionActual, 'currency'],
            ['Collections', report.totalCollections?.value, 'currency'],
            ['Production variance', variance, 'percent'],
          ]),
        },
        gavYtd: {
          data: scalarRows([
            ['Production goal', productionGoal, 'currency'],
            ['Production actual', productionActual, 'currency'],
            ['Variance', variance, 'percent'],
          ]),
        },
        gavRunRate: {
          data: scalarRows([
            ['Run rate', report.dailyRunRateVsPace?.runRate, 'currency'],
            [
              'Required pace',
              report.dailyRunRateVsPace?.requiredPace,
              'currency',
            ],
            ['Gap', report.dailyRunRateVsPace?.gap, 'currency'],
          ]),
        },
        gavVarianceTrend: {
          data: production.map((item: ReportRow, index: number) => {
            const goal = Number(productionGoals[index]?.value ?? 0)
            return {
              month: item.label,
              variance: percent(
                goal ? ((Number(item.value) - goal) / goal) * 100 : 0,
              ),
            }
          }),
        },
        gavProduction: {
          data: production.map((item: ReportRow, index: number) => {
            const goal = Number(productionGoals[index]?.value ?? 0)
            return {
              month: item.label,
              goal: currency(goal),
              actual: currency(item.value),
              variance: percent(
                goal ? ((Number(item.value) - goal) / goal) * 100 : 0,
              ),
            }
          }),
        },
        gavCollection: {
          data: collections.map((item: ReportRow, index: number) => {
            const goal = Number(collectionGoals[index]?.value ?? 0)
            return {
              month: item.label,
              goal: currency(goal),
              actual: currency(item.value),
              variance: percent(
                goal ? ((Number(item.value) - goal) / goal) * 100 : 0,
              ),
            }
          }),
        },
        gavLocation: { data: [] },
        gavProvider: { data: [] },
      }
    }
    default:
      return {}
  }
}

export function normalizeReportPayload(
  type: string,
  report: unknown,
): ReportPayloadData {
  return normalizeReportSource(type, report as ReportSource)
}

export async function loadReportPayloadData({
  clinicId,
  filters,
  metrics,
  sourceMetricsSchema,
  onProgress,
}: {
  clinicId: string
  filters: Record<string, string>
  metrics: Record<string, boolean>
  sourceMetricsSchema: MetricSchemaItem[]
  onProgress?: ProgressHandler
}): Promise<ReportPayloadData> {
  const selectedMetricIds = Object.keys(metrics).filter((id) => metrics[id])
  if (selectedMetricIds.length === 0) return {}

  const selectedTypes = Array.from(
    new Set(
      sourceMetricsSchema
        .filter((metric) => selectedMetricIds.includes(metric.id))
        .map((metric) => metric.type)
        .filter((type): type is string => Boolean(type)),
    ),
  )
  const unsupportedTypes = selectedTypes.filter(
    (type) => !REPORT_FETCHERS[type],
  )
  const supportedTypes = selectedTypes.filter((type) => REPORT_FETCHERS[type])

  const query = mapToolbarToFinanceQuery(filters, clinicId)
  if (!query) {
    throw new Error('Select a complete date range to load report data.')
  }
  const payload: ReportPayloadData = {}
  const failures = unsupportedTypes.map(
    (type) => `${type}: real report data is not available`,
  )

  await Promise.all(
    supportedTypes.map(async (type) => {
      try {
        const data = await loadCachedReport(REPORT_FETCHERS[type], query)
        const normalized = normalizeReportPayload(type, data as ReportSource)
        selectedMetricIds.forEach((metricId) => {
          if (normalized[metricId]) payload[metricId] = normalized[metricId]
        })
        onProgress?.({ ...payload })
      } catch (reason) {
        failures.push(
          reason instanceof Error ? `${type}: ${reason.message}` : type,
        )
      }
    }),
  )

  if (failures.length > 0) {
    throw new Error(
      `Some report data could not be loaded (${failures.join('; ')})`,
    )
  }
  return payload
}
